import { isAutoLayout, solid, booleanOperation, flatten, createFromSpec, matchingLayers, areSimilar, linkPreviewSpec, isLink, type LinkPreview, deleteAnchors, pathCommands, setVectorPath, type ClipboardData, type NodeId } from '@vellum/core';
import { app, type ToolId } from './app.ts';
import { resolvedTheme, setThemePref } from './theme.ts';
import { mindMapKey } from './mindmap.ts';
import { insertImage } from './canvas/interaction.ts';
import { interaction as liveInteraction } from './canvas/CanvasView.tsx';

export interface Command {
  id: string;
  label: string;
  /** e.g. "mod+shift+g" (mod = ⌘ on mac, ctrl elsewhere) */
  keys?: string[];
  run: () => void;
  enabled?: () => boolean;
}

const sel = () => app.editor.selection;
const has = () => app.editor.selection.length > 0;
const ed = () => app.editor;

let clipboard: ClipboardData | null = null;
const CLIP_PREFIX = 'vellum/clipboard:';

export async function copySelection(cut = false) {
  if (!has()) return;
  clipboard = ed().copy(sel());
  try {
    await navigator.clipboard.writeText(CLIP_PREFIX + JSON.stringify(clipboard));
  } catch {
    /* clipboard permission denied: in-memory clipboard still works */
  }
  if (cut) ed().remove(sel());
}

function pasteTarget(): NodeId {
  // Paste into the selected frame (Figma-like) when exactly one container is selected.
  const s = ed().selectedNodes();
  if (s.length === 1 && (s[0].type === 'FRAME' || s[0].type === 'SECTION' || s[0].type === 'COMPONENT')) return s[0].id;
  if (s.length === 1 && s[0].parent && app.doc.get(s[0].parent)?.type !== 'PAGE') return s[0].parent;
  return ed().pageId;
}

export async function pasteFromEvent(e: ClipboardEvent) {
  const dt = e.clipboardData;
  if (!dt) return;
  // layers copied in Figma (before images and text: Figma's clipboard can carry those too)
  const html = dt.getData('text/html');
  if (html.includes('(figma)') && html.includes('(/figma)')) {
    e.preventDefault();
    await pasteFromFigma(html);
    return;
  }
  for (const item of Array.from(dt.items)) {
    if (item.kind === 'file' && (item.type.startsWith('image/') || item.type.startsWith('video/'))) {
      const f = item.getAsFile();
      if (f) {
        e.preventDefault();
        await insertImage(app, f);
        return;
      }
    }
  }
  const text = dt.getData('text/plain');
  if (text.startsWith(CLIP_PREFIX)) {
    e.preventDefault();
    pasteData(JSON.parse(text.slice(CLIP_PREFIX.length)));
    return;
  }
  if (text.trim().startsWith('<svg')) {
    e.preventDefault();
    const { importSvg } = await import('./svgImport.ts');
    importSvg(app, text);
    return;
  }
  if (text && isLink(text) && app.pageKind() === 'WHITEBOARD') {
    // FigJam: a pasted link becomes a preview card
    e.preventDefault();
    await pasteLink(text.trim());
    return;
  }
  if (text) {
    e.preventDefault();
    const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
    const wb = app.pageKind() === 'WHITEBOARD';
    const id = wb
      ? ed().create('STICKY', { x: c.x - 120, y: c.y - 120, characters: text, authorName: app.userName })
      : ed().create('TEXT', { x: c.x, y: c.y, characters: text, textAutoResize: text.length > 60 ? 'HEIGHT' : 'WIDTH_AND_HEIGHT', width: text.length > 60 ? 400 : 0 });
    ed().setSelection([id]);
    return;
  }
  if (clipboard) pasteData(clipboard);
}

/** A link preview card at the viewport centre, with what the server could read from the page. */
async function pasteLink(url: string) {
  let meta: LinkPreview = { url };
  try {
    const r = await fetch('/api/unfurl', { method: 'POST', body: JSON.stringify({ url }), headers: { 'content-type': 'application/json' } });
    const body = (await r.json()) as LinkPreview & { error?: string };
    if (r.ok) meta = body;
    else app.toast(`No preview for this link: ${body.error ?? r.statusText}`);
  } catch {
    app.toast('No preview for this link (offline?)');
  }
  const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
  const res = createFromSpec(app.editor, linkPreviewSpec(meta, { x: Math.round(c.x - 180), y: Math.round(c.y - 140) }), pasteTarget());
  app.editor.setSelection(res.ids);
}

/** Figma's clipboard is decoded on the server (it may be zstd-compressed, which browsers can't read). */
async function pasteFromFigma(html: string) {
  try {
    const r = await fetch('/api/paste/figma', { method: 'POST', body: html, headers: { 'content-type': 'text/html' } });
    const body = (await r.json()) as { data?: ClipboardData; warnings?: string[]; error?: string };
    if (!r.ok || !body.data) {
      app.toast(`Couldn't paste from Figma: ${body.error ?? r.statusText}`);
      return;
    }
    pasteData(body.data);
    const w = body.warnings ?? [];
    if (w.length) app.toast(w.length > 1 ? `${w[0]} (and ${w.length - 1} more note${w.length > 2 ? 's' : ''})` : w[0]);
  } catch (err) {
    app.toast(`Couldn't paste from Figma: ${(err as Error).message}`);
  }
}

function pasteData(data: ClipboardData) {
  const target = pasteTarget();
  // Paste in place if the original location is visible, otherwise at the viewport centre.
  const v = app.viewportRect();
  const b = data.bounds;
  const visible = b.x < v.x + v.width && b.x + b.width > v.x && b.y < v.y + v.height && b.y + b.height > v.y;
  const ids = ed().paste(data, { parent: target, ...(visible && target === ed().pageId ? { offset: { x: 0, y: 0 } } : { at: target === ed().pageId ? { x: v.x + v.width / 2, y: v.y + v.height / 2 } : centerOf(target) }) });
  ed().setSelection(ids);
}

function centerOf(id: NodeId) {
  const b = app.doc.worldBounds(id);
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
}

export function insertTemplate(kind: 'table' | 'code') {
  const c = app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
  const cell = (text: string, header: boolean) => ({ type: 'frame', name: 'Cell', layout: 'row', width: 160, padding: [10, 12], fill: header ? '#f2f2f2' : '#ffffff', stroke: '#d9d9d9', strokeAlign: 'INSIDE' as const, children: [{ type: 'text', text, width: 'fill' as const, fontSize: 14, fontWeight: header ? 600 : 400 }] });
  const spec =
    kind === 'table'
      ? { type: 'frame', name: 'Table', x: c.x - 240, y: c.y - 70, layout: 'column', fill: '#ffffff', radius: 6, clip: true, stroke: '#d9d9d9', children: [0, 1, 2].map((r) => ({ type: 'frame', name: `Row ${r + 1}`, layout: 'row', fill: null, children: [0, 1, 2].map((col) => cell(r === 0 ? `Column ${col + 1}` : '', r === 0)) })) }
      : { type: 'frame', name: 'Code block', x: c.x - 220, y: c.y - 70, width: 440, layout: 'column', padding: 20, gap: 8, radius: 10, fill: '#1e1e2e', children: [{ type: 'text', text: 'TypeScript', fontSize: 11, fontWeight: 600, color: '#8b8ba7' }, { type: 'text', text: "function hello(name: string) {\n  return `Hello, ${name}!`;\n}", font: 'Menlo', fontSize: 14, lineHeight: 22, color: '#cdd6f4', width: 'fill' }] };
  const res = createFromSpec(app.editor, spec as never);
  app.editor.setSelection(res.ids);
}

function nudge(dx: number, dy: number) {
  if (!has()) return;
  ed().beginGesture('Nudge');
  ed().moveBy(sel(), dx, dy, 'Nudge');
  ed().endGesture();
}

function addAutoLayout() {
  const nodes = ed().selectedNodes();
  if (nodes.length === 1 && (nodes[0].type === 'FRAME' || nodes[0].type === 'COMPONENT') && !isAutoLayout(nodes[0])) {
    const n = nodes[0];
    const kids = app.doc.children(n.id);
    // guess direction from children arrangement
    let horizontal = false;
    if (kids.length > 1) {
      const xs = kids.map((k) => k.x),
        ys = kids.map((k) => k.y);
      horizontal = Math.max(...xs) - Math.min(...xs) > Math.max(...ys) - Math.min(...ys);
    }
    const sorted = [...kids].sort((a, b) => (horizontal ? a.x - b.x : a.y - b.y));
    ed().transact('Add auto layout', () => {
      ed().update(n.id, { layoutMode: horizontal ? 'HORIZONTAL' : 'VERTICAL', itemSpacing: 10, paddingTop: 10, paddingRight: 10, paddingBottom: 10, paddingLeft: 10, layoutSizingH: 'HUG', layoutSizingV: 'HUG', strokesIncludedInLayout: true });
      sorted.forEach((k, i) => ed().setZIndex(k.id, i));
    });
    return;
  }
  if (!nodes.length) return;
  ed().transact('Add auto layout', () => {
    const sorted = [...nodes].sort((a, b) => app.doc.worldBounds(a.id).y - app.doc.worldBounds(b.id).y);
    const id = ed().wrap(sel(), 'FRAME', { fills: [], name: 'Frame' });
    ed().update(id, { layoutMode: 'VERTICAL', itemSpacing: 10, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0, layoutSizingH: 'HUG', layoutSizingV: 'HUG', clipsContent: false, strokesIncludedInLayout: true });
    sorted.forEach((k, i) => ed().setZIndex(k.id, i));
    ed().setSelection([id]);
  });
}

/** Figma Draw › Create brush: the selected layer becomes a brush that strokes can use. */
const canBeBrush = () => ed().selectedNodes().length === 1 && !['PAGE', 'CONNECTOR', 'TEXT', 'SECTION'].includes(ed().selectedNodes()[0].type);
function createBrush(type: 'STRETCH' | 'SCATTER') {
  const n = ed().selectedNodes()[0];
  if (!n) return;
  ed().update(n.id, { brushDefinition: type }, type === 'STRETCH' ? 'Create stretch brush' : 'Create scatter brush');
  app.toast(`“${n.name}” is now a ${type.toLowerCase()} brush — pick it under Stroke › Brush`);
}

function selectChildren() {
  // Enter on a vector, polygon or star edits its points (as in Figma)
  const only = ed().selectedNodes();
  if (only.length === 1 && ['VECTOR', 'POLYGON', 'STAR'].includes(only[0].type) && !only[0].locked) {
    const id = only[0].type === 'VECTOR' ? only[0].id : (flatten(ed(), [only[0].id])[0] ?? only[0].id);
    ed().setSelection([id]);
    app.setUI({ editingPathId: id, pathAnchor: null, pathAnchors: [] });
    return;
  }
  const kids = ed().selectedNodes().flatMap((n) => app.doc.children(n.id).map((c) => c.id));
  if (kids.length) {
    app.setUI({ scopeId: ed().selectedNodes()[0].id });
    ed().setSelection(kids);
  }
}

function selectParent() {
  const n = ed().selectedNodes()[0];
  if (!n) return;
  const p = app.doc.get(n.parent);
  if (p && p.type !== 'PAGE') ed().setSelection([p.id]);
  else ed().setSelection([]);
  app.setUI({ scopeId: null });
}

function toggle(prop: 'visible' | 'locked') {
  const nodes = ed().selectedNodes();
  if (!nodes.length) return;
  const v = !nodes.every((n) => n[prop] === (prop === 'visible' ? false : true));
  ed().update(sel(), { [prop]: prop === 'visible' ? !v : v }, prop === 'visible' ? 'Toggle visibility' : 'Toggle lock');
}

function flip(axis: 'x' | 'y') {
  ed().transact('Flip', () => {
    for (const n of ed().selectedNodes()) ed().update(n.id, axis === 'x' ? { flipX: !n.flipX } : { flipY: !n.flipY });
  });
}

const tool = (t: ToolId) => () => app.setTool(t);

export const COMMANDS: Command[] = [
  { id: 'undo', label: 'Undo', keys: ['mod+z'], run: () => ed().undo(), enabled: () => ed().canUndo() },
  { id: 'redo', label: 'Redo', keys: ['mod+shift+z'], run: () => ed().redo(), enabled: () => ed().canRedo() },
  { id: 'copy', label: 'Copy', keys: ['mod+c'], run: () => copySelection(), enabled: has },
  { id: 'cut', label: 'Cut', keys: ['mod+x'], run: () => copySelection(true), enabled: has },
  { id: 'duplicate', label: 'Duplicate', keys: ['mod+d'], run: () => ed().duplicate(sel()), enabled: has },
  { id: 'delete', label: 'Delete', keys: ['delete', 'backspace'], run: () => ed().remove(sel()), enabled: has },
  { id: 'askAI', label: 'Ask AI…', keys: ['mod+j'], run: () => app.setUI({ assistantOpen: !app.ui.assistantOpen }) },
  {
    id: 'selectMatching',
    label: 'Select matching layers',
    keys: ['mod+alt+a'],
    run: () => {
      const n = ed().selectedNodes()[0];
      if (!n) return app.toast('Select a layer first');
      const ids = matchingLayers(app.doc, n.id);
      ed().setSelection(ids);
      if (ids.length > 1) app.toast(`${ids.length} matching layers${areSimilar(app.doc, ids) ? ' · double-click one to edit them together' : ''}`);
    },
  },
  {
    id: 'editSimilar',
    label: 'Edit similar layers together',
    run: () => {
      const sel = ed().selection;
      if (!areSimilar(app.doc, sel)) return app.toast('Select two or more similar layers (same structure) first');
      ed().multiEdit = [...sel];
      app.emit('ui');
      app.requestRender();
      app.toast(`Editing ${sel.length} layers together: changes inside one apply to all`);
    },
  },
  { id: 'selectAll', label: 'Select all', keys: ['mod+a'], run: () => ed().setSelection(app.doc.children(app.ui.scopeId ?? ed().pageId).filter((n) => n.visible && !n.locked).map((n) => n.id)) },
  { id: 'group', label: 'Group selection', keys: ['mod+g'], run: () => ed().group(sel()), enabled: has },
  { id: 'ungroup', label: 'Ungroup', keys: ['mod+shift+g', 'shift+mod+backspace'], run: () => ed().ungroup(sel()), enabled: has },
  { id: 'frameSelection', label: 'Frame selection', keys: ['mod+alt+g'], run: () => ed().wrap(sel(), 'FRAME', { fills: [solid('#ffffff')] }), enabled: has },
  { id: 'autoLayout', label: 'Add auto layout', keys: ['shift+a'], run: addAutoLayout, enabled: has },
  { id: 'component', label: 'Create component', keys: ['mod+alt+k'], run: () => ed().createComponent(sel()), enabled: has },
  { id: 'combineVariants', label: 'Combine as variants', run: () => void import('@vellum/core').then((m) => ed().setSelection([m.combineAsVariants(ed(), sel())])), enabled: () => ed().selectedNodes().length > 0 && ed().selectedNodes().every((n) => n.type === 'COMPONENT') },
  { id: 'detach', label: 'Detach instance', keys: ['mod+alt+b'], run: () => sel().forEach((id) => ed().detachInstance(id)), enabled: () => ed().selectedNodes().some((n) => n.type === 'INSTANCE') },
  { id: 'createStretchBrush', label: 'Create stretch brush', run: () => createBrush('STRETCH'), enabled: canBeBrush },
  { id: 'createScatterBrush', label: 'Create scatter brush', run: () => createBrush('SCATTER'), enabled: canBeBrush },
  { id: 'mask', label: 'Use as mask', keys: ['mod+alt+m'], run: () => ed().useAsMask(sel()), enabled: () => sel().length > 1 },
  { id: 'union', label: 'Union selection', keys: ['mod+alt+u'], run: () => booleanOperation(ed(), sel(), 'UNION'), enabled: has },
  { id: 'subtract', label: 'Subtract selection', keys: ['mod+alt+-'], run: () => booleanOperation(ed(), sel(), 'SUBTRACT'), enabled: () => sel().length > 1 },
  { id: 'intersect', label: 'Intersect selection', keys: ['mod+alt+i'], run: () => booleanOperation(ed(), sel(), 'INTERSECT'), enabled: () => sel().length > 1 },
  { id: 'exclude', label: 'Exclude selection', keys: ['mod+alt+x'], run: () => booleanOperation(ed(), sel(), 'EXCLUDE'), enabled: () => sel().length > 1 },
  { id: 'flatten', label: 'Flatten', keys: ['mod+e'], run: () => flatten(ed(), sel()), enabled: has },
  { id: 'section', label: 'Wrap in section', keys: ['mod+alt+s'], run: () => ed().wrap(sel(), 'SECTION'), enabled: has },
  { id: 'bringToFront', label: 'Bring to front', keys: [']'], run: () => ed().bringToFront(sel()), enabled: has },
  { id: 'bringForward', label: 'Bring forward', keys: ['mod+]'], run: () => ed().bringForward(sel()), enabled: has },
  { id: 'sendBackward', label: 'Send backward', keys: ['mod+['], run: () => ed().sendBackward(sel()), enabled: has },
  { id: 'sendToBack', label: 'Send to back', keys: ['['], run: () => ed().sendToBack(sel()), enabled: has },
  { id: 'alignLeft', label: 'Align left', keys: ['alt+a'], run: () => ed().align(sel(), 'LEFT'), enabled: has },
  { id: 'alignCenterH', label: 'Align horizontal centers', keys: ['alt+h'], run: () => ed().align(sel(), 'CENTER_H'), enabled: has },
  { id: 'alignRight', label: 'Align right', keys: ['alt+d'], run: () => ed().align(sel(), 'RIGHT'), enabled: has },
  { id: 'alignTop', label: 'Align top', keys: ['alt+w'], run: () => ed().align(sel(), 'TOP'), enabled: has },
  { id: 'alignCenterV', label: 'Align vertical centers', keys: ['alt+v'], run: () => ed().align(sel(), 'CENTER_V'), enabled: has },
  { id: 'alignBottom', label: 'Align bottom', keys: ['alt+s'], run: () => ed().align(sel(), 'BOTTOM'), enabled: has },
  { id: 'distributeH', label: 'Distribute horizontal spacing', keys: ['alt+shift+h'], run: () => ed().distribute(sel(), 'H'), enabled: () => sel().length > 2 },
  { id: 'tidyUp', label: 'Tidy up', keys: ['ctrl+alt+t'], run: () => ed().tidyUp(sel()), enabled: () => sel().length > 1 },
  { id: 'distributeV', label: 'Distribute vertical spacing', keys: ['alt+shift+v'], run: () => ed().distribute(sel(), 'V'), enabled: () => sel().length > 2 },
  { id: 'flipH', label: 'Flip horizontal', keys: ['shift+h'], run: () => flip('x'), enabled: has },
  { id: 'flipV', label: 'Flip vertical', keys: ['shift+v'], run: () => flip('y'), enabled: has },
  { id: 'toggleVisible', label: 'Show/Hide', keys: ['mod+shift+h'], run: () => toggle('visible'), enabled: has },
  { id: 'toggleLock', label: 'Lock/Unlock', keys: ['mod+shift+l'], run: () => toggle('locked'), enabled: has },
  { id: 'selectChildren', label: 'Select children', keys: ['enter'], run: selectChildren, enabled: has },
  { id: 'selectParent', label: 'Select parent', keys: ['shift+enter', '\\'], run: selectParent, enabled: has },
  { id: 'zoomIn', label: 'Zoom in', keys: ['mod+=', '='], run: () => app.setZoom(app.camera.zoom * 2) },
  { id: 'zoomOut', label: 'Zoom out', keys: ['mod+-', '-'], run: () => app.setZoom(app.camera.zoom / 2) },
  { id: 'zoom100', label: 'Zoom to 100%', keys: ['shift+0', 'mod+0'], run: () => app.setZoom(1) },
  { id: 'zoomFit', label: 'Zoom to fit', keys: ['shift+1'], run: () => app.zoomToFit() },
  { id: 'zoomSelection', label: 'Zoom to selection', keys: ['shift+2'], run: () => app.zoomToNodes(sel()), enabled: has },
  { id: 'chat', label: 'Cursor chat', keys: ['/'], run: () => app.setUI({ chatOpen: true }) },
  { id: 'script', label: 'Script console', keys: ['mod+alt+p'], run: () => app.setUI({ scriptOpen: true }) },
  { id: 'find', label: 'Find and replace', keys: ['mod+f'], run: () => app.setUI({ findOpen: true }) },
  { id: 'toggleRulers', label: 'Show/Hide rulers', keys: ['alt+r'], run: () => app.setUI({ showRulers: !app.ui.showRulers }) },
  { id: 'outlineMode', label: 'Outline mode', keys: ['mod+y'], run: () => (app.setUI({ outlineMode: !app.ui.outlineMode }), app.requestRender()) },
  { id: 'darkMode', label: 'Dark mode', keys: ['mod+alt+shift+d'], run: () => (setThemePref(resolvedTheme() === 'dark' ? 'light' : 'dark'), app.emit('ui')) },
  { id: 'devMode', label: 'Toggle Dev Mode', keys: ['shift+d'], run: () => (app.setUI({ devMode: !app.ui.devMode }), app.setTool('select')) },
  { id: 'toggleGrids', label: 'Show/Hide layout grids', keys: ['shift+g'], run: () => app.setUI({ showGrids: !app.ui.showGrids }) },
  { id: 'shortcuts', label: 'Keyboard shortcuts', keys: ['shift+/'], run: () => app.setUI({ shortcutsOpen: true }) },
  { id: 'quickActions', label: 'Quick actions', keys: ['mod+k', 'mod+/'], run: () => app.setUI({ quickActionsOpen: true }) },
  { id: 'toggleUI', label: 'Show/Hide UI', keys: ['mod+\\'], run: () => app.setUI({ showLayers: !app.ui.showLayers, showProps: !app.ui.showProps }) },
  { id: 'rename', label: 'Rename', keys: ['mod+r'], run: () => app.startRename(), enabled: () => sel().length === 1 && !app.readOnly },
  { id: 'present', label: 'Present', keys: ['alt+enter', 'mod+alt+enter'], run: () => void import('./ui/Present.tsx').then((m) => m.startPresentation()) },
  { id: 'tool.select', label: 'Move', keys: ['v'], run: tool('select') },
  { id: 'tool.lasso', label: 'Lasso', keys: ['q'], run: tool('lasso') },
  { id: 'tool.hand', label: 'Hand', keys: ['h'], run: tool('hand') },
  { id: 'tool.frame', label: 'Frame', keys: ['f', 'a'], run: tool('frame') },
  { id: 'tool.section', label: 'Section', keys: ['shift+s'], run: tool('section') },
  { id: 'tool.rect', label: 'Rectangle', keys: ['r'], run: tool('rect') },
  { id: 'tool.ellipse', label: 'Ellipse', keys: ['o'], run: tool('ellipse') },
  { id: 'tool.line', label: 'Line', keys: ['l'], run: tool('line') },
  { id: 'tool.arrow', label: 'Arrow', keys: ['shift+l'], run: tool('arrow') },
  { id: 'tool.pen', label: 'Pen', keys: ['p'], run: tool('pen') },
  { id: 'tool.pencil', label: 'Pencil', keys: ['shift+p', 'm'], run: tool('pencil') },
  { id: 'tool.text', label: 'Text', keys: ['t'], run: tool('text') },
  { id: 'tool.sticky', label: 'Sticky note', keys: ['s'], run: tool('sticky') },
  { id: 'tool.shape', label: 'Shape', keys: ['shift+r'], run: tool('shape') },
  { id: 'tool.connector', label: 'Connector', keys: ['x', 'shift+c'], run: tool('connector') },
  { id: 'tool.mindmap', label: 'Mind map', keys: ['shift+m'], run: tool('mindmap') },
  { id: 'tool.image', label: 'Place image', keys: ['mod+shift+k'], run: tool('image') },
  { id: 'tool.comment', label: 'Comment', keys: ['c'], run: tool('comment') },
  { id: 'tool.stamp', label: 'Stamp', keys: ['e'], run: tool('stamp') },
  { id: 'tool.eraser', label: 'Eraser', keys: ['shift+backspace', 'shift+e'], run: tool('eraser') },
  { id: 'templates', label: 'Templates…', run: () => app.setUI({ templatesOpen: true }) },
  { id: 'insertTable', label: 'Insert table', run: () => insertTemplate('table') },
  { id: 'insertCode', label: 'Insert code block', run: () => insertTemplate('code') },
];

const byId = new Map(COMMANDS.map((c) => [c.id, c]));
export function command(id: string) {
  return byId.get(id)!;
}

function keyString(e: KeyboardEvent): string {
  const parts: string[] = [];
  const mac = navigator.platform.toLowerCase().includes('mac');
  if (mac ? e.metaKey : e.ctrlKey) parts.push('mod');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  let k = e.key.toLowerCase();
  if (e.code.startsWith('Key')) k = e.code.slice(3).toLowerCase();
  else if (e.code.startsWith('Digit')) k = e.code.slice(5);
  else if (e.code === 'BracketLeft') k = '[';
  else if (e.code === 'BracketRight') k = ']';
  else if (e.code === 'Backslash') k = '\\';
  else if (e.code === 'Equal') k = '=';
  else if (e.code === 'Minus') k = '-';
  else if (e.code === 'Slash') k = '/';
  parts.push(k);
  return parts.join('+');
}

export function formatKeys(keys?: string[]): string {
  if (!keys?.length) return '';
  const mac = navigator.platform.toLowerCase().includes('mac');
  return keys[0]
    .split('+')
    .map((p) => (p === 'mod' ? (mac ? '⌘' : 'Ctrl+') : p === 'shift' ? '⇧' : p === 'alt' ? (mac ? '⌥' : 'Alt+') : p === 'backspace' ? '⌫' : p === 'delete' ? '⌦' : p === 'enter' ? '↵' : p.toUpperCase()))
    .join('');
}

const keymap = new Map<string, Command>();
for (const c of COMMANDS) for (const k of c.keys ?? []) keymap.set(k, c);

export function installKeyboard() {
  // sliders, checkboxes and buttons don't take text, so shortcuts still apply while they have focus
  const NON_TEXT = new Set(['range', 'checkbox', 'radio', 'button', 'color']);
  const isTyping = (t: EventTarget | null) => t instanceof HTMLElement && ((t instanceof HTMLInputElement && !NON_TEXT.has(t.type)) || t.tagName === 'TEXTAREA' || t.isContentEditable);
  // Escape closes the dialog on top, as clicking beside it does (before fields inside it see the key)
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const backdrop = [...document.querySelectorAll('.modal-backdrop')].at(-1);
      if (!backdrop) return;
      e.preventDefault();
      e.stopPropagation();
      backdrop.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }));
    },
    true,
  );
  window.addEventListener('keydown', (e) => {
    if (isTyping(e.target)) return;
    // a focused control keeps the keys it uses itself
    if (e.target instanceof HTMLInputElement && (e.key.startsWith('Arrow') || e.key === ' ' || e.key === 'Home' || e.key === 'End')) return;
    // While a text editor is opening, keystrokes belong to it, not to shortcuts.
    if (app.ui.editingTextId) {
      const ta = document.querySelector<HTMLTextAreaElement>('.text-editor');
      if (ta) ta.focus();
      return;
    }
    const inter = liveInteraction;
    if (e.code === 'Space' && !e.repeat) {
      app.setUI({ spaceDown: true });
      e.preventDefault();
      return;
    }
    if (app.ui.croppingId && (e.key === 'Escape' || e.key === 'Enter')) {
      app.setUI({ croppingId: null });
      e.preventDefault();
      return;
    }
    // Vector edit mode keys
    if (app.ui.editingPathId) {
      const n = app.doc.get(app.ui.editingPathId);
      if (e.key === 'Escape' || e.key === 'Enter') {
        app.setUI({ editingPathId: null, pathAnchor: null, pathAnchors: [] });
        e.preventDefault();
        return;
      }
      const picked = app.ui.pathAnchors.length ? app.ui.pathAnchors : app.ui.pathAnchor !== null ? [app.ui.pathAnchor] : [];
      if ((e.key === 'Backspace' || e.key === 'Delete') && n && picked.length) {
        setVectorPath(ed(), n.id, deleteAnchors(pathCommands(n.path), picked), picked.length > 1 ? 'Delete points' : 'Delete point');
        app.setUI({ pathAnchor: null, pathAnchors: [] });
        e.preventDefault();
        return;
      }
    }
    // mind maps: Tab adds a subtopic, Enter a sibling, and you keep typing
    if ((e.key === 'Tab' || e.key === 'Enter') && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey && ed().selection.length === 1 && mindMapKey(e.key, ed().selection[0])) {
      e.preventDefault();
      return;
    }
    if (e.key === 'Escape') {
      if (inter?.cancel()) return;
      if (app.ui.contextMenu) return app.setUI({ contextMenu: null });
      if (app.tool !== 'select') return app.setTool('select');
      if (sel().length) return selectParent();
      return;
    }
    if (e.key === 'Enter' && app.tool === 'pen') {
      inter?.finishPen(false);
      return;
    }
    if (e.key.startsWith('Arrow') && has()) {
      e.preventDefault();
      const d = e.shiftKey ? 10 : 1;
      if (e.key === 'ArrowLeft') nudge(-d, 0);
      if (e.key === 'ArrowRight') nudge(d, 0);
      if (e.key === 'ArrowUp') nudge(0, -d);
      if (e.key === 'ArrowDown') nudge(0, d);
      return;
    }
    // Enter on a single text-like node starts editing
    if (e.key === 'Enter' && !e.shiftKey && sel().length === 1) {
      const n = ed().selectedNodes()[0];
      if (n.type === 'TEXT' || n.type === 'STICKY' || n.type === 'SHAPE_WITH_TEXT') {
        e.preventDefault();
        app.setUI({ editingTextId: n.id });
        return;
      }
    }
    const c = keymap.get(keyString(e));
    if (c && (!c.enabled || c.enabled())) {
      e.preventDefault();
      c.run();
    }
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === 'Space') app.setUI({ spaceDown: false });
    if (e.key === 'Alt') app.setUI({ altDown: false });
  });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Alt' && !app.ui.altDown) app.setUI({ altDown: true });
  });
  window.addEventListener('blur', () => app.setUI({ altDown: false, spaceDown: false }));
  window.addEventListener('paste', (e) => {
    if (isTyping(e.target)) return;
    void pasteFromEvent(e);
  });
  window.addEventListener('copy', (e) => {
    if (isTyping(e.target) || !has()) return;
    e.preventDefault();
    clipboard = ed().copy(sel());
    e.clipboardData?.setData('text/plain', CLIP_PREFIX + JSON.stringify(clipboard));
  });
  window.addEventListener('cut', (e) => {
    if (isTyping(e.target) || !has()) return;
    e.preventDefault();
    clipboard = ed().copy(sel());
    e.clipboardData?.setData('text/plain', CLIP_PREFIX + JSON.stringify(clipboard));
    ed().remove(sel());
  });
}
